import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  ListTodo,
  RefreshCw,
  Filter,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Play,
  Pause,
  ArrowLeft
} from 'lucide-react';

// Status configuration - defined outside component
const statusConfig = {
  'pending': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  'in_progress': { color: 'bg-blue-100 text-blue-800', icon: Play },
  'completed': { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  'failed': { color: 'bg-red-100 text-red-800', icon: XCircle },
  'awaiting_input': { color: 'bg-purple-100 text-purple-800', icon: Pause },
  'cancelled': { color: 'bg-gray-100 text-gray-800', icon: XCircle }
};

const priorityConfig = {
  'critical': 'bg-red-600 text-white',
  'high': 'bg-orange-500 text-white',
  'medium': 'bg-yellow-500 text-white',
  'low': 'bg-gray-400 text-white'
};

// Status Icon Component - defined outside main component
const StatusIcon = ({ status }) => {
  const config = statusConfig[status] || statusConfig['pending'];
  const Icon = config.icon;
  return <Icon className="w-4 h-4" />;
};

const AutonomyTasks = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [taskMeta, setTaskMeta] = useState({ types: [], statuses: [], priorities: [] });
  
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    priority: searchParams.get('priority') || '',
    task_type: searchParams.get('task_type') || '',
    requires_review: searchParams.get('requires_review') || '',
    skip: parseInt(searchParams.get('skip')) || 0,
    limit: 20
  });

  const fetchTaskMeta = useCallback(async () => {
    try {
      const res = await api.get('/admin/autonomy/tasks/types');
      setTaskMeta({
        types: res.data.task_types || [],
        statuses: res.data.statuses || [],
        priorities: res.data.priorities || []
      });
    } catch (error) {
      console.error('Failed to fetch task metadata:', error);
    }
  }, []);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('skip', filters.skip);
      params.set('limit', filters.limit);
      if (filters.status) params.set('status', filters.status);
      if (filters.priority) params.set('priority', filters.priority);
      if (filters.task_type) params.set('task_type', filters.task_type);
      if (filters.requires_review) params.set('requires_review', filters.requires_review);
      
      const res = await api.get(`/admin/autonomy/tasks?${params.toString()}`);
      setTasks(res.data.tasks || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchTaskMeta();
  }, [fetchTaskMeta]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value, skip: 0 };
    setFilters(newFilters);
    
    const params = new URLSearchParams();
    if (newFilters.status) params.set('status', newFilters.status);
    if (newFilters.priority) params.set('priority', newFilters.priority);
    if (newFilters.task_type) params.set('task_type', newFilters.task_type);
    if (newFilters.requires_review) params.set('requires_review', newFilters.requires_review);
    setSearchParams(params);
  };

  const handlePageChange = (direction) => {
    const newSkip = direction === 'next' 
      ? filters.skip + filters.limit 
      : Math.max(0, filters.skip - filters.limit);
    setFilters({ ...filters, skip: newSkip });
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="autonomy-tasks-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <Link to="/admin/autonomy" className="text-gray-600 hover:text-gray-900">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-900">AI Tasks</h1>
                <p className="text-sm text-gray-500">Tasks queued for AI processing</p>
              </div>
            </div>
            <Button onClick={fetchTasks} variant="outline" size="sm" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
                data-testid="filter-status"
              >
                <option value="">All Statuses</option>
                {taskMeta.statuses.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>

              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
                data-testid="filter-priority"
              >
                <option value="">All Priorities</option>
                {taskMeta.priorities.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>

              <select
                value={filters.task_type}
                onChange={(e) => handleFilterChange('task_type', e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
                data-testid="filter-task-type"
              >
                <option value="">All Types</option>
                {taskMeta.types.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={filters.requires_review === 'true'}
                  onChange={(e) => handleFilterChange('requires_review', e.target.checked ? 'true' : '')}
                  data-testid="filter-requires-review"
                />
                Requires Review Only
              </label>

              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setFilters({ status: '', priority: '', task_type: '', requires_review: '', skip: 0, limit: 20 });
                  setSearchParams({});
                }}
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tasks List */}
        <Card>
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2">
              <ListTodo className="w-5 h-5" />
              Tasks ({total})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
              </div>
            ) : tasks.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <ListTodo className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No tasks found</p>
              </div>
            ) : (
              <div className="divide-y">
                {tasks.map((task) => (
                  <div key={task.id} className="p-4 hover:bg-gray-50 transition-colors" data-testid={`task-${task.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-full ${statusConfig[task.status]?.color || 'bg-gray-100'}`}>
                          <StatusIcon status={task.status} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{task.task_name || task.task_type}</span>
                            {task.requires_human_review && (
                              <Badge className="bg-amber-100 text-amber-800">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Needs Review
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{task.task_description || 'No description'}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className={statusConfig[task.status]?.color || 'bg-gray-100'}>
                              {task.status}
                            </Badge>
                            {task.priority && (
                              <Badge className={priorityConfig[task.priority] || 'bg-gray-400'}>
                                {task.priority}
                              </Badge>
                            )}
                            {task.assigned_agent && (
                              <Badge variant="outline">{task.assigned_agent}</Badge>
                            )}
                          </div>
                          {task.entity_type && (
                            <p className="text-xs text-gray-400 mt-2">
                              {task.entity_type} - {task.entity_id?.slice(0, 8) || 'N/A'}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-400">
                          Created: {task.created_at ? new Date(task.created_at).toLocaleString() : '-'}
                        </p>
                        {task.due_at && (
                          <p className="text-xs text-orange-500 mt-1">
                            Due: {new Date(task.due_at).toLocaleString()}
                          </p>
                        )}
                        {task.retry_count > 0 && (
                          <p className="text-xs text-red-500 mt-1">
                            Retries: {task.retry_count}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-500">
            Showing {filters.skip + 1} - {Math.min(filters.skip + filters.limit, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={filters.skip === 0}
              onClick={() => handlePageChange('prev')}
              data-testid="prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={filters.skip + filters.limit >= total}
              onClick={() => handlePageChange('next')}
              data-testid="next-page"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AutonomyTasks;
